import React from 'react'
import { useDispatch } from 'react-redux'
import { AppRoutes } from '@core/AppRoutes'
import { setUnsupportedBrowser } from '@core/Areas/KambiClient/Store'
import customerSettings from '@core/Areas/KambiClient/config/customerSettings'
import { getStartupParameters } from '@core/Areas/KambiClient/config/startupParameters'
import widgetSettings from '@core/Areas/KambiClient/config/widgetSettings'
import {
  handleKambiLogin,
  handleKambiLogout,
  isKambiSupportedBrowser,
  isUserAccountStatePending,
  isUserResidentOfSA,
  kambiLog,
  setInitialVisibilityState,
  waitForWapi,
} from '@core/Areas/KambiClient/helpers'
import { registerKambiCustomWidgets } from '@core/Areas/KambiClient/widgets/register'
import { queryClient } from '@core/Data/ReactQuery'
import { serverConfigQuery } from '@core/Data/ServerConfig/query'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { logError } from '@core/Utils/logging/logging'
import { appHistory } from '@core/Utils/routing/history'
import { handleClientSubscriptions } from './helpers/handleClientSubscriptions'
import { BootstrapJurisdiction, handleScriptLoading } from './helpers/handleScriptLoading'

let hasLoadedScript = false

export const useKambiClient = (): void => {
  const dispatch = useDispatch()

  const bootstrapJurisdictionRef = React.useRef<BootstrapJurisdiction>()
  const [isPendingReloadForJurisdiction, setIsPendingReloadForJurisdiction] = React.useState(false)

  // ==================================
  // Handle Pending Reload After SignUp
  // ==================================
  React.useEffect(() => {
    if (!isPendingReloadForJurisdiction) return
    const dispose = appHistory.listen(() => {
      if (!isInSignUp()) location.reload()
    })
    return () => dispose()
  }, [isPendingReloadForJurisdiction])

  // ============
  // Load Scripts
  // ============
  React.useEffect(() => {
    if (hasLoadedScript || bootstrapJurisdictionRef.current) return

    if (!isKambiSupportedBrowser()) {
      dispatch(setUnsupportedBrowser())
      return
    }

    hasLoadedScript = true

    queryClient.fetchQuery(serverConfigQuery).then(({ serverEnvironment }) => {
      window._kc = getStartupParameters(serverEnvironment)
      window.customerSettings = customerSettings
      window.widgetSettings = widgetSettings

      registerKambiCustomWidgets()

      handleScriptLoading()
        .then(bootstrapJurisdiction => {
          bootstrapJurisdictionRef.current = bootstrapJurisdiction
        })
        .catch(() => {
          logError('Error loading Kambi scripts', true)
        })
    })
  }, [dispatch])

  // ==================================
  // Register WAPI when widget is ready
  // ==================================
  React.useEffect(() => {
    if (window.wapi) return

    waitForWapi().then(wapi => {
      // Set initial betslip state minimized + sets up subscription
      wapi.set(wapi.BETSLIP_MAXIMIZED, { maximized: false })
      wapi.request(wapi.USER_DATA)
      wapi.request(wapi.BETSLIP_OUTCOMES)

      setInitialVisibilityState(wapi)

      wapi.subscribe(response => {
        kambiLog('client event', response)
        handleClientSubscriptions(response)
      })
    })
  }, [])

  // ================================================
  // Handle Authentication and Bootstrap Jurisdiction
  // ================================================
  React.useEffect(() => {
    let shouldTryLogout: boolean

    const subscription = userAccountState$
      .skipWhile(isUserAccountStatePending)
      .map(userAccountState => ({
        accountNumber: userAccountState.accountNumber,
        isLoggedIn: userAccountState.isLoggedIn,
        residentialDetail: userAccountState.residentialDetail,
      }))
      .distinctUntilChanged(
        s => s,
        (a, b) => JSON.stringify(a) === JSON.stringify(b)
      )
      .subscribe(({ residentialDetail, isLoggedIn, accountNumber }) => {
        shouldTryLogout = !!isLoggedIn

        if (isLoggedIn && accountNumber && residentialDetail) {
          handleKambiLogin(accountNumber, residentialDetail)
        } else {
          handleKambiLogout()
        }

        if (!bootstrapJurisdictionRef.current) return

        const requiredBootstrap: BootstrapJurisdiction = isUserResidentOfSA(residentialDetail)
          ? 'SA'
          : 'WA'

        if (requiredBootstrap !== bootstrapJurisdictionRef.current) {
          if (isInSignUp()) {
            setIsPendingReloadForJurisdiction(true)
          } else {
            window.location.reload()
          }
        }
      })

    return () => {
      if (shouldTryLogout) handleKambiLogout()
      subscription.dispose()
    }
  }, [dispatch])
}

// =============
// Local Helpers
// =============

function isInSignUp(): boolean {
  return location.pathname.startsWith(AppRoutes.SignUp)
}
